import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { VisualizationCanvasStage, KIND_STYLE } from './VisualizationCanvasStage';
import { StateKeyValueEditor } from './StateKeyValueEditor';

import type { Step, View, ViewKind } from '@/features/Visualizer/types';

interface Props {
  steps: Step[];
  onChange: (next: Step[]) => void;
  /** Reports which step (0-based, within this variant) is currently on the stage, so the live Player preview can follow the edit point instead of defaulting to the first step. */
  onFocusStep?: (stepIndex: number) => void;
}

/** Palette entries a trainer clicks to add to the stage. `linkedlist` is intentionally excluded — see `VisualizationEditor`'s `RawJsonItemEditor` doc comment on why it has no visual canvas yet. */
const PALETTE: Array<{ kind: ViewKind; label: string; group: 'Data structures' | 'Content'; makeDefault: () => View }> = [
  { kind: 'array', label: 'Array', group: 'Data structures', makeDefault: () => ({ kind: 'array', values: [1, 2, 3] }) },
  { kind: 'stack', label: 'Stack', group: 'Data structures', makeDefault: () => ({ kind: 'stack', values: [1, 2, 3] }) },
  { kind: 'grid', label: 'Grid', group: 'Data structures', makeDefault: () => ({ kind: 'grid', values: [[1, 2], [3, 4]] }) },
  {
    kind: 'tree',
    label: 'Tree',
    group: 'Data structures',
    makeDefault: () => ({ kind: 'tree', rootId: 'root', nodes: [{ id: 'root', value: 1, children: [] }] }),
  },
  { kind: 'graph', label: 'Graph', group: 'Data structures', makeDefault: () => ({ kind: 'graph', nodes: [{ id: 'a' }, { id: 'b' }], edges: [] }) },
  { kind: 'note', label: 'Note', group: 'Content', makeDefault: () => ({ kind: 'note', label: 'New element', attributes: {} }) },
  {
    kind: 'diagram',
    label: 'Diagram',
    group: 'Content',
    makeDefault: () => ({
      kind: 'diagram',
      nodes: [
        { id: 'a', label: 'Node A', shape: 'box', tone: 'muted', attributes: {}, x: 40, y: 80 },
        { id: 'b', label: 'Node B', shape: 'box', tone: 'muted', attributes: {}, x: 260, y: 80 },
      ],
      edges: [{ id: 'edge-1', source: 'a', target: 'b', directed: true }],
    }),
  },
];

const PALETTE_GROUPS = ['Data structures', 'Content'] as const;

/** No `makeDefault`/`onClick` — a pointer can only be created by dropping it directly onto a specific
 * cell inside an Array block's own canvas (`VisualizationArrayGraphCanvas`'s drop handler), since a
 * pointer has no meaning without a cell to target. */
const ARRAY_ELEMENT_PALETTE = [{ key: 'pointer', label: 'Pointer' }] as const;

/**
 * `steps[i]` can be a bare `{}` — e.g. a step whose creation predates this
 * component (the old "+ Add" pushed `{}` directly), or one still being
 * authored via the raw-JSON fallback elsewhere. Every read of `current`
 * below goes through this so `views`/`narration` are never `undefined`.
 */
function withDefaults(step: Step | undefined): Step {
  return { ...step, narration: step?.narration ?? '', views: step?.views ?? [] };
}

/**
 * Replaces the old "each step is its own box, each box has its own nested
 * Views section" layout with what a trainer actually pictures: ONE
 * persistent stage they drag elements onto, and a filmstrip below it for
 * moving between steps — stepping through narrates changes to the SAME
 * stage over time, exactly like the Player itself plays back. Duplicating a
 * step (see `VisualizationEditor`'s step-duplication feature) is what makes
 * this coherent: each new step starts as an exact copy of the stage, so the
 * trainer only edits what changed instead of rebuilding from scratch.
 *
 * This component manages the entire `steps` array as plain objects and never
 * routes through JSON Forms `Control`/`Dispatch` internally — narration,
 * state, and views all have dedicated hand-built editors now, so there's
 * nothing left for the generic per-field JSON Forms machinery to usefully do
 * here.
 */
export function VisualizationStageEditor({ steps, onChange, onFocusStep }: Props) {
  const [currentIndex, setCurrentIndex] = useState(() => Math.max(0, steps.length - 1));

  const safeIndex = Math.min(currentIndex, Math.max(0, steps.length - 1));
  const current = withDefaults(steps[safeIndex]);

  useEffect(() => {
    onFocusStep?.(safeIndex);
  }, [safeIndex, onFocusStep]);

  const updateCurrentStep = (patch: Partial<Step>) => {
    // An empty `steps` array has nothing for `.map` to update — the first
    // edit (typing narration, adding a view) creates step 1.
    const next =
      steps.length === 0
        ? [{ ...current, ...patch }]
        : steps.map((s, i) => (i === safeIndex ? { ...withDefaults(s), ...patch } : s));
    onChange(next);
  };

  const addStep = () => {
    const next = [...steps, { ...structuredClone(current), narration: '' }];
    onChange(next);
    setCurrentIndex(next.length - 1);
  };

  const removeStep = (index: number) => {
    const next = steps.filter((_, i) => i !== index);
    onChange(next);
    setCurrentIndex((i) => Math.min(i, Math.max(0, next.length - 1)));
  };

  const addView = (kind: ViewKind) => {
    const entry = PALETTE.find((p) => p.kind === kind);
    if (entry) {
      updateCurrentStep({ views: [...current.views, entry.makeDefault()] });
    }
  };

  const [pendingNewViewPosition, setPendingNewViewPosition] = useState<{
    index: number;
    position: { x: number; y: number };
  } | null>(null);

  const addViewAt = (kind: ViewKind, flowPosition: { x: number; y: number }) => {
    const entry = PALETTE.find((p) => p.kind === kind);
    if (!entry) return;
    updateCurrentStep({ views: [...current.views, entry.makeDefault()] });
    setPendingNewViewPosition({ index: current.views.length, position: flowPosition });
  };

  return (
    <div className="mb-3">
      <div className="flex gap-3">
        <div className="w-28 shrink-0 space-y-1.5">
          <p className="text-[11px] font-medium text-[#374151]">Add to stage</p>
          {PALETTE_GROUPS.map((group) => (
            <div key={group} className="space-y-1">
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">{group}</p>
              {PALETTE.filter((p) => p.group === group).map((p) => {
                const Icon = KIND_STYLE[p.kind].icon;
                return (
                  <button
                    key={p.kind}
                    type="button"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('application/x-kodprep-view-kind', p.kind)}
                    onClick={() => addView(p.kind)}
                    className="flex w-full select-none items-center gap-1.5 rounded-md border border-dashed border-primary/50 bg-primary/5 px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-primary/10"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          ))}

          <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">Array elements</p>
          {ARRAY_ELEMENT_PALETTE.map((el) => (
            <button
              key={el.key}
              type="button"
              draggable
              onDragStart={(e) => e.dataTransfer.setData('application/x-kodprep-array-element', el.key)}
              className="flex w-full select-none items-center gap-1.5 rounded-md border border-dashed border-primary/50 bg-primary/5 px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-primary/10"
            >
              {el.label}
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div data-testid="visualization-stage" className="min-w-0 flex-1">
            <VisualizationCanvasStage
              views={current.views}
              onChangeView={(vIndex, next) =>
                updateCurrentStep({ views: current.views.map((v, i) => (i === vIndex ? next : v)) })
              }
              onRemoveView={(vIndex) => {
                updateCurrentStep({ views: current.views.filter((_, i) => i !== vIndex) });
              }}
              onDuplicateView={(vIndex) => {
                updateCurrentStep({
                  views: [
                    ...current.views.slice(0, vIndex + 1),
                    structuredClone(current.views[vIndex]),
                    ...current.views.slice(vIndex + 1),
                  ],
                });
              }}
              pendingNewViewPosition={pendingNewViewPosition}
              onConsumedPendingPosition={() => setPendingNewViewPosition(null)}
              onDropViewKind={(kind, pos) => addViewAt(kind, pos)}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[#374151]" htmlFor="stage-narration">
              Narration<span className="ml-0.5 text-red-500">*</span>
            </label>
            <textarea
              id="stage-narration"
              value={current.narration}
              onChange={(e) => updateCurrentStep({ narration: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
              placeholder="What's happening in this frame?"
            />
          </div>

          <div className="rounded-md border border-[#e5e7eb] p-2">
            <StateKeyValueEditor value={current.state} onChange={(next) => updateCurrentStep({ state: next })} />
          </div>

          <div>
            <p className="mb-1 text-[11px] font-medium text-[#374151]">Steps</p>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {steps.map((s, i) => (
                <div key={i} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setCurrentIndex(i)}
                    className={`w-28 rounded-md border px-2 py-1.5 text-left text-[11px] ${
                      i === safeIndex
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-[#d1d5db] text-[#374151] hover:border-primary/50'
                    }`}
                  >
                    <span className="block font-semibold">#{i + 1}</span>
                    <span className="block truncate">{s.narration || '(no narration)'}</span>
                  </button>
                  {steps.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove step ${i + 1}`}
                      onClick={() => removeStep(i)}
                      className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-destructive shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStep}
                className="shrink-0 rounded-md border border-dashed border-[#d1d5db] px-3 py-1.5 text-xs text-primary hover:border-primary"
                title="Duplicates the current step's stage"
              >
                + Step
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
